import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../App";
import { Mail, Lock, User, ArrowRight, AlertCircle, ShieldCheck, ChevronDown } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sqDropdownOpen, setSqDropdownOpen] = useState(false);

  // Capture plan intent from URL query param (e.g. /register?plan=decide) and persist it
  // so we can show a "complete your subscription" banner after login.
  useEffect(() => {
    const planIntent = searchParams.get("plan");
    if (planIntent === "decide" || planIntent === "avanza") {
      try { localStorage.setItem("_ft_plan_intent", planIntent); } catch (e) { /* private mode */ }
    }
  }, [searchParams]);

  const securityQuestions = [
    t("sq_pet"), t("sq_city"), t("sq_teacher"), t("sq_food"), t("sq_friend")
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("register_password_error")); return; }
    if (!/^[A-Za-z0-9]+$/.test(password)) { setError(t("register_password_alphanumeric")); return; }
    if (!securityQuestion || !securityAnswer.trim()) { setError(t("register_security_required")); return; }
    if (!gdprConsent) { setError(t("register_gdpr_required")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        name, email, password,
        security_question: securityQuestion,
        security_answer: securityAnswer,
        gdpr_consent: true
      }, { withCredentials: true });
      // Redirect to email verification
      navigate("/verify-email", { state: { email } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Map backend Pydantic validator codes to friendly i18n messages
      const mapMsg = (raw) => {
        if (!raw) return "";
        const s = typeof raw === "string" ? raw : (raw.msg || JSON.stringify(raw));
        if (s.includes("password_alphanumeric_only")) return t("register_password_alphanumeric");
        if (s.includes("password_too_short")) return t("register_password_error");
        return s;
      };
      if (typeof detail === "string") setError(mapMsg(detail));
      else if (Array.isArray(detail)) setError(detail.map(mapMsg).join(" "));
      else setError("Error de registro");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex">
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-[#5B21B6] to-[#002FA7] p-12">
        <div className="max-w-lg text-center">
          <img src="https://static.prod-images.emergentagent.com/jobs/f2610779-0a83-474d-b9cb-16a028eed818/images/121034ac787eac55cb54d87663667bb360616532cbf2b11032febc8d62c417c7.png" alt="FUTURA Decision" className="w-full max-w-md mx-auto mb-8" />
          <h2 className="font-['Outfit'] text-2xl font-semibold text-white mb-4">{t("register_right_title")}</h2>
          <p className="text-white/80">{t("register_right_subtitle")}</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-12">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1.5">
                <FuturaIcon className="w-full h-full text-white" />
              </div>
              <span className="font-['Outfit'] font-semibold text-xl tracking-tight text-[#111111]">FUTURA</span>
            </Link>
            <LanguageSelector variant="light" />
          </div>

          <div className="animate-fade-in">
            <h1 className="font-['Outfit'] text-3xl font-semibold text-[#111111] tracking-tight mb-2">{t("register_title")}</h1>
            <p className="text-[#6B7280] mb-8">{t("register_subtitle")}</p>

            {error && (
              <div className="flex items-center gap-2 p-4 mb-6 bg-[#EF4444]/10 text-[#EF4444] rounded-xl" data-testid="register-error">
                <AlertCircle className="w-5 h-5 flex-shrink-0" /><p className="text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#111111] font-medium">{t("register_name")}</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("register_name_placeholder")} className="pl-12 h-12 bg-white border-[#E5E5EA] rounded-xl focus:border-[#002FA7] focus:ring-[#002FA7]" required autoComplete="name" data-testid="register-name-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#111111] font-medium">{t("register_email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" className="pl-12 h-12 bg-white border-[#E5E5EA] rounded-xl focus:border-[#002FA7] focus:ring-[#002FA7]" required autoComplete="email" data-testid="register-email-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#111111] font-medium">{t("register_password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value.replace(/[^A-Za-z0-9]/g, ""))} placeholder={t("register_password_placeholder")} className="pl-12 h-12 bg-white border-[#E5E5EA] rounded-xl focus:border-[#002FA7] focus:ring-[#002FA7]" required minLength={6} pattern="[A-Za-z0-9]{6,}" autoComplete="new-password" data-testid="register-password-input" />
                </div>
                <p className="text-xs text-[#6B7280]" data-testid="register-password-hint">{t("register_password_hint")}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="securityQuestion" className="text-[#111111] font-medium">{t("register_security_question")}</Label>
                <div className="relative" data-testid="register-security-question">
                  <button type="button" onClick={() => setSqDropdownOpen(!sqDropdownOpen)} className="w-full h-12 bg-white text-left border border-[#E5E5EA] rounded-xl px-4 text-sm flex items-center justify-between" style={{color: securityQuestion ? '#111111' : '#6B7280'}}>
                    <span className="truncate">{securityQuestion || t("register_select_question")}</span>
                    <ChevronDown className="w-4 h-4 text-[#6B7280] flex-shrink-0 ml-2" />
                  </button>
                  {sqDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E5EA] rounded-xl shadow-lg overflow-hidden" style={{zIndex: 50}}>
                      {securityQuestions.map((q, i) => (
                        <button key={`sq-${i}`} type="button" onClick={() => { setSecurityQuestion(q); setSqDropdownOpen(false); }} className="w-full text-left px-4 py-3 text-sm hover:bg-[#F5F5F7] border-b border-[#E5E5EA] last:border-b-0" style={{color: '#111111'}}>{q}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="securityAnswer" className="text-[#111111] font-medium">{t("register_security_answer")}</Label>
                <div className="relative">
                  <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <Input id="securityAnswer" type="text" value={securityAnswer} onChange={(e) => setSecurityAnswer(e.target.value)} placeholder={t("register_security_answer_placeholder")} className="pl-12 h-12 bg-white text-[#111111] border-[#E5E5EA] rounded-xl focus:border-[#002FA7] focus:ring-[#002FA7]" style={{color: '#111111', WebkitTextFillColor: '#111111'}} required data-testid="register-security-answer" />
                </div>
                <p className="text-[10px] text-amber-600 leading-snug">{t("register_security_warning")}</p>
              </div>

              {/* GDPR Consent */}
              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="gdpr-consent-toggle" onClick={() => setGdprConsent(!gdprConsent)}>
                  <div className={`w-5 h-5 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 transition-all ${gdprConsent ? "bg-[#002FA7] border-[#002FA7]" : "border-[#D1D5DB] bg-white"}`}>
                    {gdprConsent && <svg className="w-3 h-3 text-white" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" fill="none" /></svg>}
                  </div>
                  <span className="text-xs text-[#6B7280] leading-relaxed">
                    {t("register_gdpr_text")}{" "}
                    <Link to="/terminos" target="_blank" className="text-[#002FA7] underline">{t("register_gdpr_terms")}</Link>{" "}
                    {t("register_gdpr_and")}{" "}
                    <Link to="/privacidad" target="_blank" className="text-[#002FA7] underline">{t("register_gdpr_privacy")}</Link>.
                  </span>
                </label>
              </div>

              <Button type="submit" disabled={loading || !gdprConsent} className="w-full h-12 bg-[#002FA7] hover:bg-[#001d6a] text-white font-medium rounded-xl btn-lift" data-testid="register-submit-btn">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <>{t("register_submit")}<ArrowRight className="ml-2 w-5 h-5" /></>}
              </Button>
            </form>

            <p className="text-center mt-8 text-[#6B7280]">
              {t("register_has_account")}{" "}
              <Link to="/login" className="text-[#002FA7] font-medium hover:underline" data-testid="register-login-link">{t("register_login_link")}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
